import { Box, LinearProgress } from "@mui/material";
import { useEffect } from "react";
import { useNavigation } from "react-router-dom";
import { useBoolean, useCounter, useInterval, useTimeout } from "usehooks-ts";

// Note: The component is a migration from nprogress, so we try to reproduce the same kind of animation.
const TICK = 200;
const FADE = 300;
const SPEED = 0.03; // <= adjust this to control trickle speed (0.01 = slower, 0.05 = faster)

export const NavigationIndicator = () => {
  const { state } = useNavigation();
  const loading = state === "loading" || state === "submitting";

  const { value: visible, setTrue: show, setFalse: hide } = useBoolean(false);
  const { count, setCount } = useCounter(0);

  useTimeout(
    () => {
      hide();
      setCount(0);
    },
    !loading && visible ? FADE + 50 : null,
  );

  useEffect(() => {
    if (loading) {
      show();
      setCount((v) => (v > 0 && v < 100 ? v : 8)); // if progress is already in progress, keep it; otherwise start at 8%
    } else if (visible) {
      setCount(100);
    }
  }, [loading, visible, show, setCount]);

  useInterval(
    // on each tick: move closer to 100%
    // - always add at least 0.3%
    // - otherwise add a fraction of the remaining distance (scaled by SPEED)
    // - cap at 99% so it never finishes until navigation completes
    () => setCount((v) => Math.min(99, v + Math.max(0.3, (100 - v) * SPEED))),
    loading ? TICK : null,
  );

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: (t) => t.zIndex.modal + 1,
        visibility: loading || visible ? "visible" : "hidden",
        opacity: loading ? 1 : 0,
        transition: `opacity ${FADE}ms ease`,
        pointerEvents: "none",
        willChange: "opacity",
      }}
    >
      <LinearProgress
        variant="determinate"
        value={count}
        sx={{
          height: 2,
          backgroundColor: "transparent",
          "& .MuiLinearProgress-bar": {
            backgroundColor: (t) => t.palette.secondary[2],
            boxShadow: (t) =>
              `0 0 10px ${t.palette.secondary[2]}, 0 0 5px ${t.palette.secondary[2]}`,
            transition: "width 200ms linear, transform 200ms linear",
          },
        }}
      />
    </Box>
  );
};
